import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, combineLatestWith, filter, map, Observable, of, shareReplay } from 'rxjs';
import { IUserRoleVm } from "@app/core/services/WebRegApiClient";

const ANONYMOUS: Session = null;
const CACHE_SIZE = 1;

@Injectable({providedIn: 'root'})
export class AuthenticationService {
  private session$: Observable<Session> | null = null;
  private jwt$: Observable<string> | null = null;
  private roles$: Observable<IUserRoleVm[]> | null = null;

  constructor(private http: HttpClient) {
  }

  public getRoles(ignoreCache: boolean = false): Observable<IUserRoleVm[]> {
    if (!this.roles$ || ignoreCache) {
      this.roles$ = this.http.get<IUserRoleVm[]>('api/users/roles').pipe(
        catchError(err => {
          return of([]);
        }),
        shareReplay(CACHE_SIZE)
      );
    }
    return this.roles$;
  }

  public getJwt() {
    this.jwt$ = this.http.get('bff/jwt', {responseType: 'text'}).pipe(
      catchError(err => {
        return of("err");
      }),
      shareReplay(CACHE_SIZE)
    );

    return this.jwt$;
  }

  public getSession(ignoreCache: boolean = false) {
    if (!this.session$ || ignoreCache) {
      this.session$ = this.http.get<Session>('bff/user').pipe(
        catchError(err => {
          return of(ANONYMOUS);
        }),
        shareReplay(CACHE_SIZE)
      );
    }
    return this.session$;
  }

  public getIsAuthenticated(ignoreCache: boolean = false) {
    return this.getSession(ignoreCache).pipe(
      map(UserIsAuthenticated)
    );
  }

  public getIsAnonymous(ignoreCache: boolean = false) {
    return this.getSession(ignoreCache).pipe(
      map(UserIsAnonymous)
    );
  }

  public getUsername(ignoreCache: boolean = false) {
    return this.getSession(ignoreCache).pipe(
      filter(UserIsAuthenticated),
      map(s => s.find(c => c.type === 'name')?.value)
    );
  }

  public getUscId(ignoreCache: boolean = false) {
    return this.getSession(ignoreCache).pipe(
      filter(UserIsAuthenticated),
      map(s => s.find(c => c.type === 'USCID')?.value)
    );
  }

  public getIsAdmin(ignoreCache: boolean = false): Observable<boolean> {
    return this.isRequiredRole('Admin', ignoreCache);
  }

  public getIsDev(ignoreCache: boolean = false): Observable<boolean> {
    return this.isRequiredRole('Dev', ignoreCache);
  }

  private isRequiredRole(roleName: string, ignoreCache: boolean = false): Observable<boolean> {
    return this.getSession(ignoreCache).pipe(
      combineLatestWith(this.getRoles(ignoreCache)),
      map(([s, r]) => {
        const claimRole = s?.find(c => c.type === 'role') ?? undefined;
        const userRole = r?.find(x => x.roleName?.toLowerCase() === (claimRole?.value.toLowerCase() ?? ''));
        const requiredRole = r?.find(x => x.roleName?.toLowerCase() === roleName.toLowerCase());

        if (!userRole || !requiredRole) {
          return false;
        }

        return (userRole?.roleLevel ?? 0) >= (requiredRole?.roleLevel ?? 99)
      })
    );
  }

  public getLogoutUrl(ignoreCache: boolean = false) {
    return this.getSession(ignoreCache).pipe(
      filter(UserIsAuthenticated),
      map(s => s.find(c => c.type === 'auth:logout_url')?.value)
    );
  }
}

export interface Claim {
  type: string;
  value: string;
}

export type Session = Claim[] | null;

function UserIsAuthenticated(s: Session): s is Claim[] {
  return s !== null;
}

function UserIsAnonymous(s: Session): s is null {
  return s === null;
}
